import React, { useState, useEffect } from 'react';
import ServiceInformation from './serviceInformation';
import Availability from './availability';
import Location from './location';
import Gallery from './gallery';
import EditSeo from './seo';
import supabase from '../../../../supabaseClient';
import { useNavigate } from "react-router-dom";

type AdditionalRow = { id: number; additionalService: number | null; price: number; duration: string, speciality: boolean, image?: string | null; };
type Option = { id: number; name: string };
type TagOption = { id: number; name: string };
type Info = {
  title: string;
  since: string;
  masterCategory: Option[];   // city in UI
  category: Option[];              // sectors (multi)
  mainCategory: Option[];          // main categories (multi)
  subCategory: Option[];           // sub categories (multi)
  tags: TagOption[];               // tags (multi)
  subTags: TagOption[];            // sub tags (multi)
  description: string;
  additionalEnabled: boolean;
  additional: AdditionalRow[];
  videoUrl: string;
  accessibility?: string[];
  payment_options?: string[];
  service_mode?: string[];
  price_range?: string[];
  quality?: string[];
  sp_niche?: string[];
  cuisine?: string[];
  is_brand: boolean;       // ✅ add this
  brand_name: string | null; // ✅ add this
};


type Slot = { id: number; from: string; to: string; slots: string };
type PerDay = {
  monday: Slot[]; tuesday: Slot[]; wednesday: Slot[]; thursday: Slot[];
  friday: Slot[]; saturday: Slot[]; sunday: Slot[];
};

type DaySchedule = {
  closed: boolean;
  slots: Slot[];
};

type AvailabilityT = {
  all: Slot[];
  perDay: {
    monday: DaySchedule;
    tuesday: DaySchedule;
    wednesday: DaySchedule;
    thursday: DaySchedule;
    friday: DaySchedule;
    saturday: DaySchedule;
    sunday: DaySchedule;
  };
};



type LocationT = {
  address?: string;
  lat?: number;
  lng?: number;
};

type GalleryT = {
  files: (File | { url: string })[]; // support File (new) and existing urls if editing
};

type SeoT = {
  slug: string;              // unique constraint in DB recommended
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string[];
};

type AddServiceForm = {
  info: Info;
  availability: AvailabilityT;
  location: LocationT;
  gallery: GalleryT;
  seo: SeoT;
};
const emptySlot: Slot = { id: 1, from: '00:00:00', to: '00:00:00', slots: '' };
const emptyDay: DaySchedule = { closed: false, slots: [{ ...emptySlot }] };
const initialForm: AddServiceForm = {
  info: {
    title: '',
    since: '',
    masterCategory: [],
    category: [],
    mainCategory: [],
    subCategory: [],
    tags: [],
    subTags: [],
    description: '',
    additionalEnabled: true,
    additional: [{ id: 1, additionalService: null, price: 0, duration: '', speciality: false, image: null }],
    videoUrl: '',
    accessibility: [],
    payment_options: [],
    service_mode: [],
    price_range: [],
    quality: [],
    sp_niche: [],
    cuisine: [],
    is_brand: false,       // ✅ default
    brand_name: null
  },
  availability: {
    all: [{ ...emptySlot }],
    perDay: {
      monday: { ...emptyDay },
      tuesday: { ...emptyDay },
      wednesday: { ...emptyDay },
      thursday: { ...emptyDay },
      friday: { ...emptyDay },
      saturday: { ...emptyDay },
      sunday: { ...emptyDay },
    },
  },
  location: {},
  gallery: { files: [] },
  seo: { slug: '', metaKeywords: [] },
};


const AddService = () => {
  const [TabChange, setTabChange] = useState(true);
  const [TabChange1, setTabChange1] = useState(false);
  const [TabChange2, setTabChange2] = useState(false);
  const [TabChange3, setTabChange3] = useState(false);
  const [TabChange4, setTabChange4] = useState(false);
  const [PageChange, setPageChange] = useState('information');
  const [form, setForm] = useState<AddServiceForm>(initialForm);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.phone) {
        const dbMobile = session.user.phone.replace(/^\+?91/, '');
        const { data, error } = await supabase
          .from('rd_users')
          .select('*')
          .eq('mobile', dbMobile)
          .single();
        //console.log(data);
        setUserRole(error ? null : data?.role || null);
      } else {
        //setProfile(null);
      }
    };
    fetchUser();
  }, []);
  const availabilityTab = () => {
    setPageChange('booking');
    setTabChange1(true);
    setTabChange(false);
    setTabChange2(false);
    setTabChange3(false);
    setTabChange4(false);
  };
  const serviceTab = () => {
    setPageChange('information');
    setTabChange1(false);
    setTabChange2(false);
    setTabChange3(false);
    setTabChange4(false);
    setTabChange(true);
  };
  const locationTab = () => {
    setPageChange('location');
    setTabChange2(true);
    setTabChange1(false);
    setTabChange(false);
    setTabChange3(false);
    setTabChange4(false);
  };

  const galleryTab = () => {
    setPageChange('gallery');
    setTabChange3(true);
    setTabChange2(false);
    setTabChange1(false);
    setTabChange4(false);
    setTabChange(false);
  };

  const seoTab = () => {
    setPageChange('seo');
    setTabChange4(true);
    setTabChange3(false);
    setTabChange2(false);
    setTabChange1(false);
    setTabChange(false);
  };
  // ---- helpers passed to children to update slices ----
  const updateInfo = (patch: Partial<Info>) =>
    setForm(prev => ({ ...prev, info: { ...prev.info, ...patch } }));

  const updateAvailability = (patch: Partial<AvailabilityT>) =>
    setForm(prev => ({ ...prev, availability: { ...prev.availability, ...patch } }));

  const updateLocation = (patch: Partial<LocationT>) =>
    setForm(prev => ({ ...prev, location: { ...prev.location, ...patch } }));

  const updateGallery = (updater: (g: GalleryT) => GalleryT) =>
    setForm(prev => ({ ...prev, gallery: updater(prev.gallery) }));

  const updateSeo = (patch: Partial<SeoT>) =>
    setForm(prev => ({ ...prev, seo: { ...prev.seo, ...patch } }));

  // ---- uploader for gallery files (optional) ----
  async function uploadGalleryFiles(files: GalleryT['files']): Promise<string[]> {
    const urls: string[] = [];

    for (const f of files) {
      if (f instanceof File) {
        const ext = f.name.split('.').pop() || 'bin';
        const path = `services/${crypto.randomUUID()}.${ext}`;
        const { error } = await supabase.storage.from('Service Gallery').upload(path, f, {
          cacheControl: '3600',
          upsert: false,
        });
        if (error) throw error;

        const { data } = supabase.storage.from('Service Gallery').getPublicUrl(path);
        urls.push(data.publicUrl);
      } else if ('url' in f) {
        urls.push(f.url);
      }
    }
    return urls;
  }
  async function uploadServiceImages(additional: AdditionalRow[]): Promise<AdditionalRow[]> {
    const updated = [];
    for (const row of additional) {
      if (row.image && row.image.startsWith("data:")) {
        // convert base64 to file
        const res = await fetch(row.image);
        const blob = await res.blob();
        const ext = blob.type.split("/")[1] || "png";
        const path = `services/${crypto.randomUUID()}.${ext}`;
        const { error } = await supabase.storage
          .from("Service Gallery")
          .upload(path, blob, { cacheControl: "3600", upsert: false });
        if (error) throw error;
        const { data } = supabase.storage.from("Service Gallery").getPublicUrl(path);
        updated.push({ ...row, image: data.publicUrl });
      } else {
        updated.push(row);
      }
    }
    return updated;
  }


  // ---- final save (called from SEO step) ----
  const handleSave = async () => {
    setSaving(true);
    setErrorMsg(null);

    try {
      // validations
      if (!form.info.title?.trim()) throw new Error('Title is required.');
      //if (!form.seo.slug?.trim()) throw new Error('Slug is required.');

      // Auth (recommended so RLS passes)
      const { data: { user }, error: userErr } = await supabase.auth.getUser();
      if (userErr) throw userErr;
      if (!user) throw new Error('You must be signed in to save.');

      // upload gallery
      const galleryUrls = await uploadGalleryFiles(form.gallery.files);
      const additionalWithUrls = await uploadServiceImages(form.info.additional);
      // normalize availability: copy "all" to any empty day
      const fillAvailability = () => {
        const all = form.availability.all ?? [];
        const per = { ...form.availability.perDay };

        (Object.keys(per) as (keyof typeof per)[]).forEach((k) => {
          if (!Array.isArray(per[k].slots) || per[k].slots.length === 0) {
            per[k] = { ...per[k], slots: all.map((s, i) => ({ ...s, id: i + 1 })) };
          }
        });

        return { all, perDay: per };
      };


      const payload = {
        user_id: user.id,
        // information
        title: form.info.title,
        since: form.info.since,
        description: form.info.description || null,
        // store NAMES to match your current TEXT columns
        city_id: form.info.masterCategory.map(o => o.id),
        sector_ids: form.info.category.map(o => o.id),
        main_category_ids: form.info.mainCategory.map(o => o.id),
        sub_category_ids: form.info.subCategory.map(o => o.id),
        //tag_ids: form.info.tags.map(o => o.id),
        // sub_tag_ids: form.info.subTags.map(o => o.id),
        additional_enabled: !!form.info.additionalEnabled,
        //additional: form.info.additional?.length ? form.info.additional : [],
        // 👇 now includes uploaded image URLs
        additional: additionalWithUrls,
        video_url: form.info.videoUrl || null,
        // price: form.info.price ?? null,
        // availability
        availability: fillAvailability(),
        // location
        address: form.location.address || null,
        lat: form.location.lat ?? null,
        lng: form.location.lng ?? null,

        // gallery
        gallery_urls: galleryUrls,

        // seo
        slug: form.seo.slug,
        meta_title: form.seo.metaTitle || null,
        meta_description: form.seo.metaDescription || null,
        meta_keywords: form.seo.metaKeywords ?? [],
        is_brand: form.info.is_brand,
        brand_name: form.info.brand_name ?? null,
        status: 'draft',
        // 🆕 NEW: Extra details stored in JSON
        extra_details: {
          accessibility: form.info.accessibility ?? [],
          payment_options: form.info.payment_options ?? [],
          service_mode: form.info.service_mode ?? [],
          price_range: form.info.price_range ?? [],
          quality: form.info.quality ?? [],
          sp_niche: form.info.sp_niche ?? [],
          cuisine: form.info.cuisine ?? []
        }

      };


      const { data, error } = await supabase
        .from('listings')           // 👈 new table
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      setForm(initialForm);
      alert('Listing saved successfully!');
      navigate("/services/all-services");
    } catch (e: any) {
      console.error(e);
      setErrorMsg(e.message ?? 'Failed to save listing.');
    } finally {
      setSaving(false);
    }
  };
  console.log(form);
  //console.log(userRole);
  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-lg-12 m-auto">
              <div className="progressbar-card">
                <ul id="progress-head">
                  <li className="active">
                    Add Listing - Listing Information
                  </li>
                  <li>Add Listing - Availablity</li>
                  <li>Add Listing - Location</li>
                  <li>Add Listing - Gallery</li>
                  <li>Add Listing - SEO</li>
                </ul>
                <ul id="progressbar">
                  <li
                    className={PageChange === "information" ? "active" : ""}
                    onClick={() => serviceTab()}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="multi-step-icon">
                      <span><i className="far fa-check-circle" /></span>
                    </div>
                    <div className="multi-step-info"><h6>Information</h6></div>
                  </li>

                  <li
                    className={PageChange === "booking" ? "active" : ""}
                    onClick={() => availabilityTab()}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="multi-step-icon">
                      <span><i className="far fa-clock" /></span>
                    </div>
                    <div className="multi-step-info"><h6>Availability</h6></div>
                  </li>

                  <li
                    className={PageChange === "location" ? "active" : ""}
                    onClick={() => locationTab()}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="multi-step-icon">
                      <span><i className="far fa-map" /></span>
                    </div>
                    <div className="multi-step-info"><h6>Location</h6></div>
                  </li>

                  <li
                    className={PageChange === "gallery" ? "active" : ""}
                    onClick={() => galleryTab()}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="multi-step-icon">
                      <span><i className="far fa-images" /></span>
                    </div>
                    <div className="multi-step-info"><h6>Gallery</h6></div>
                  </li>

                  {userRole === "A1" && (
                    <li
                      className={PageChange === "seo" ? "active" : ""}
                      onClick={() => seoTab()}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="multi-step-icon">
                        <span><i className="far fa-chart-bar" /></span>
                      </div>
                      <div className="multi-step-info"><h6>SEO</h6></div>
                    </li>
                  )}
                </ul>

              </div>
            </div>
          </div>

          {errorMsg && (
            <div className="row"><div className="col-lg-12 m-auto">
              <div className="alert alert-danger">{errorMsg}</div>
            </div></div>
          )}



          <div className="row">
            <div className="col-lg-12 m-auto">
              {PageChange === 'information' ? (
                <ServiceInformation
                  value={form.info}
                  onChange={(patch) => setForm(p => ({ ...p, info: { ...p.info, ...patch } }))}
                  nextTab={availabilityTab}
                />

              ) : PageChange === 'booking' ? (
                <Availability
                  value={form.availability}
                  onChange={(patch) =>
                    setForm((p) => ({ ...p, availability: { ...p.availability, ...patch } }))
                  }
                  prevTab={serviceTab}
                  nextTab={locationTab}
                />

              ) : PageChange === 'location' ? (
                <Location
                  value={form.location} // { address?: string; lat?: number; lng?: number }
                  onChange={(patch) => setForm(p => ({ ...p, location: { ...p.location, ...patch } }))}
                  prevTab={availabilityTab}
                  nextTab={galleryTab}
                />

              ) : PageChange === 'gallery' ? (
                // <Gallery
                //   value={form.gallery}                 // { files: (File | {url:string})[] }
                //   onChange={(updater) => setForm(p => ({ ...p, gallery: updater(p.gallery) }))}
                //   prevTab={locationTab}
                //   nextTab={seoTab}
                // />
                <Gallery
                  value={form.gallery}
                  onChange={(updater) => setForm(p => ({ ...p, gallery: updater(p.gallery) }))}
                  prevTab={locationTab}
                  nextTab={() => {
                    if (userRole === "A1") {
                      seoTab(); // go to SEO step
                    } else {
                      handleSave(); // directly save after Gallery if Admin
                    }
                  }}
                  saving={saving}
                  userRole={userRole}
                />
              ) : (
                userRole === "A1" && (
                  <EditSeo
                    value={form.seo}
                    onChange={updateSeo}
                    prevTab={galleryTab}
                    onSave={handleSave}
                    saving={saving}
                  />
                )

              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddService;
